<script lang="ts">
  import Form from "../components/Form.svelte";
  import ManualEntry from "./PaymentInfoTask/ManualEntry.svelte";
  import { domId } from "../lib/dom";
  import { onMount } from "svelte";

  const ViewState = {
    INITIAL: 'initial',
    WORKFLOW: 'workflow',
    VERIFICATION_SUCCESS: 'verification_success',
    VERIFICATION_FAILURE: 'verification_failure',
    MANUAL_ENTRY: 'manual_entry'
  };

  let {
    action,
    errors,
    task,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    task: {
      tinkUrl: string;
      rateLimitMessage: string;
      preventRetry?: boolean;
    };
  } = $props();

  let bankAccount = $state({
    bankProvider: "",
    accountHolderName: "",
    sortCode: "",
    accountNumber: ""
  })

  let errorMessage = $state("");
  let tryAgainVisible = $state(true);
  let currentView = $state(defineInitialView());

  let actionButtonDisabled = $derived(
    currentView !== ViewState.MANUAL_ENTRY && currentView !== ViewState.VERIFICATION_SUCCESS
  );

  const closeDialogId = domId("close-dialog");

  function preventRetryView() {
    tryAgainVisible = false;
    errorMessage = task.rateLimitMessage;
    return ViewState.VERIFICATION_FAILURE;
  }

  function defineInitialView() {
    return task.preventRetry ? preventRetryView() : ViewState.INITIAL;
  }

  function launchLink() {
    currentView = ViewState.WORKFLOW;
    window.open(task.tinkUrl, "_blank");
  }

  function showManualForm() {
    currentView = ViewState.MANUAL_ENTRY;
  }

  function tryAgain() {
    currentView = defineInitialView();
  }

  function cancel() {
    if (currentView === ViewState.MANUAL_ENTRY) {
      tryAgain();
    }
  }

  // event.data is constructed in the PaymentInfoTask.callback_message method
  function handleWorkflowMessage(event) {
    if (event.origin !== window.origin) return;
    const { status, message, preventRetry } = event.data || {};
    if (status === "success") {
      currentView = ViewState.VERIFICATION_SUCCESS;
    } else if (message) {
      errorMessage = message;
      currentView = ViewState.VERIFICATION_FAILURE;
      tryAgainVisible = !preventRetry;
    }
  }

  onMount(() => {
    window.addEventListener("message", handleWorkflowMessage);
    return () => {
      window.removeEventListener("message", handleWorkflowMessage);
    };
  });
</script>

<Form method="patch" {action} class="ui-rhythm">
  {#if currentView === ViewState.INITIAL}
    <!-- Initial Content -->
    <h2 class="ui-title">Provide banking details</h2>
    <p>
      To process your payment, we'll need to verify your banking details. You'll be redirected to Tink, our secure banking partner, to safely provide your account information.
    </p>
    <div class="flex flex-col items-center gap-4">
      <button type="button" class="btn btn-primary" onclick={launchLink}>
        Link your bank account
      </button>
      <button type="button" class="link text-primary" onclick={showManualForm}>
        Enter details manually instead
      </button>
    </div>

  {:else if currentView === ViewState.WORKFLOW}
    <!-- Workflow Content -->
    <h2 class="ui-title">Complete the Tink workflow</h2>
    <div>
      <div class="flex justify-center">
        <div class="loading loading-spinner loading-lg text-primary"></div>
      </div>
      <p class="mt-4">
        Please follow the prompts in the Tink workflow to verify your banking details. This screen will update once completed.
      </p>
    </div>

  {:else if currentView === ViewState.VERIFICATION_SUCCESS}
    <!-- Verification Complete Content -->
    <h2 class="ui-title">Verification complete</h2>
    <p>
      Thank you for providing your banking information. You can now close this window.
    </p>

  {:else if currentView === ViewState.VERIFICATION_FAILURE}
    <!-- Verification Error Content -->
    <h2 class="ui-title">Verification failed</h2>
    <div class="ui-alert ui-alert-warning mb-4">
      <p>{errorMessage}</p>
    </div>
    {#if tryAgainVisible}
      <p>
        Please <button type="button" onclick={tryAgain} class="link text-primary">try again</button> or contact support if the issue persists.
      </p>
    {/if}
    <button type="button" class="link text-primary" onclick={showManualForm}>
      Enter details manually instead
    </button>

  {:else if currentView === ViewState.MANUAL_ENTRY}
    <!-- Manual Entry Form -->
    <ManualEntry bind:bankAccount {errors} />
  {/if}

  <div class="floating-footer {currentView === ViewState.VERIFICATION_SUCCESS ? "justify-end" : ""}">
    {#if currentView === ViewState.MANUAL_ENTRY}
      <button class="btn btn-ghost" onclick={cancel} type="button">Back</button>
    {:else if currentView !== ViewState.VERIFICATION_SUCCESS}
      <button class="btn btn-ghost" form={closeDialogId}>Cancel</button>
    {/if}
    <button class="btn btn-primary" type="submit" disabled={actionButtonDisabled}>Done</button>
  </div>
</Form>

<form method="dialog" id={closeDialogId}></form>
